import type { StaffItineraryOutcome } from "@lafayette/schemas";
import { useState } from "react";

import { emptyNoteClass, hintClass } from "../../requests/components/parts";
import { Button } from "../../ui/button";
import { CheckboxField, FieldGroup, SelectField, TextAreaField, TextField } from "../../ui/fields";
import { Notice } from "../../ui/notice";
import { SENSITIVITY_LABELS } from "../labels";
import {
  ADDABLE_KINDS,
  type AddableKind,
  addItemOperation,
  changesOf,
  type EditForm,
  editFormOf,
  editItemErrors,
  editItemOperation,
  emptyNewItem,
  type FieldErrors,
  type MoveForm,
  moveErrors,
  moveFormOf,
  moveItemOperation,
  type NewItemForm,
  newItemErrors,
  removeItemOperation,
} from "../operations";
import { useCatalogSearch } from "../queries";
import { dayHeading, type Item, type Section, touchesConsequentialItem } from "../view-model";
import { ItineraryCommandDialog, useItineraryCommandForm } from "./CommandDialog";
import {
  focusFirstInvalidInDialog,
  initialSave,
  type PlanContext,
  reasonNeeded,
  SaveSettings,
  type SaveState,
  sendOperations,
} from "./save";
import { TimingFields, WallTimeFields, ZoneDatalist } from "./TimeFields";

// The item dialogs: add, edit, move, remove. Each builds the operation list of the edit commands and sends it with the version
// tokens of the plan the page read (`save.tsx`). A refusal that needs the user (a clock gap, a repeated time, a reason) keeps the
// dialog open; a stale plan ends it with "This plan changed" and the page reads the plan again.

type Done = (outcome: StaffItineraryOutcome) => void;

const KIND_OPTIONS: readonly { value: AddableKind; label: string }[] = [
  { value: "CATALOG_ITEM", label: "Catalog item (published)" },
  { value: "CUSTOM_ACTIVITY", label: "Custom activity or place (typed words)" },
  { value: "TRANSIT", label: "Travel, for example a flight" },
  { value: "BUFFER", label: "Buffer time" },
];

const STATUS_OPTIONS = [
  { value: "PLANNED", label: "Planned" },
  { value: "TENTATIVE", label: "Tentative" },
] as const;

const SENSITIVITY_OPTIONS = (["NONE", "OUTDOOR", "WATER", "HEIGHT"] as const).map((value) => ({
  value,
  label: SENSITIVITY_LABELS[value] ?? value,
}));

const dayOptions = (sections: readonly Section[]) =>
  sections.map((section) => ({ value: section.id, label: dayHeading(section) }));

/** The shared item fields: flags and the two notes. The staff note sits in its own "Staff only" block. */
function FlagFields({
  value,
  onChange,
}: Readonly<{
  value: Pick<NewItemForm, "isFixed" | "weatherSensitivity" | "customerNote" | "staffNote">;
  onChange: (patch: Partial<NewItemForm>) => void;
}>) {
  return (
    <>
      <CheckboxField
        id="item-fixed"
        label="Fixed: a commitment the customer holds"
        checked={value.isFixed}
        onChange={(isFixed) => onChange({ isFixed })}
        hint="Changing a fixed item later needs a staff reason."
      />
      <SelectField
        id="item-weather"
        label="Weather sensitivity"
        value={value.weatherSensitivity}
        onChange={(next) =>
          onChange({ weatherSensitivity: next as NewItemForm["weatherSensitivity"] })
        }
        options={SENSITIVITY_OPTIONS}
      />
      <TextAreaField
        id="item-customer-note"
        label="Customer note"
        value={value.customerNote}
        onChange={(customerNote) => onChange({ customerNote })}
        rows={2}
        maxLength={1000}
        hint="The customer reads this."
      />
      <div className="grid gap-3 rounded-sm border-2 border-dashed border-info-border bg-info-soft p-3">
        <p className="m-0 text-xs font-bold tracking-wider text-info uppercase">Staff only</p>
        <TextAreaField
          id="item-staff-note"
          label="Staff note"
          value={value.staffNote}
          onChange={(staffNote) => onChange({ staffNote })}
          rows={2}
          maxLength={1000}
          hint="The customer never sees this."
        />
      </div>
    </>
  );
}

// --- add ----------------------------------------------------------------------------------------------------------------------

type AddProps = Readonly<{
  context: PlanContext;
  sections: readonly Section[];
  initialSectionId: string;
  onDone: Done;
  onClose: () => void;
}>;

export function AddItemDialog({ context, sections, initialSectionId, onDone, onClose }: AddProps) {
  const [sectionId, setSectionId] = useState(initialSectionId);
  const section = sections.find((entry) => entry.id === sectionId) ?? sections[0] ?? null;
  const [form, setForm] = useState<NewItemForm>(() => emptyNewItem(section));
  const [save, setSave] = useState<SaveState>(initialSave);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [words, setWords] = useState("");
  const [searched, setSearched] = useState("");
  const [chosenName, setChosenName] = useState("");
  const command = useItineraryCommandForm<StaffItineraryOutcome>(onDone);
  const search = useCatalogSearch(searched);

  const patch = (next: Partial<NewItemForm>) => setForm((current) => ({ ...current, ...next }));
  const changeDay = (id: string) => {
    setSectionId(id);
    const next = sections.find((entry) => entry.id === id);
    if (next !== undefined && form.timing.kind === "SCHEDULED") {
      patch({
        timing: {
          ...form.timing,
          start: {
            ...form.timing.start,
            date: next.localDate ?? form.timing.start.date,
            timeZone: next.timeZone ?? form.timing.start.timeZone,
            disambiguation: "reject",
          },
        },
      });
    }
  };

  const reasonRequired = reasonNeeded(
    context,
    save,
    false,
    command.failure?.kind === "needs_reason",
  );

  function submit() {
    if (section === null) return;
    const found: Record<string, string> = { ...newItemErrors(form) };
    if (reasonRequired && save.reason.trim() === "") found["itinerary-reason"] = "Add a reason.";
    setErrors(found);
    if (Object.keys(found).length > 0) return focusFirstInvalidInDialog();
    void command.submit(() => sendOperations(context, [addItemOperation(section, form)], save));
  }

  return (
    <ItineraryCommandDialog
      stale={context.stale}
      title="Add an item"
      description="Add one item to the plan. Times are clock times in a zone you choose; the server works out the exact instant."
      confirmLabel="Add item"
      command={command}
      onSubmit={submit}
      onClose={onClose}
    >
      <ZoneDatalist />
      <SelectField
        id="item-day"
        label="Day"
        value={sectionId}
        onChange={changeDay}
        options={dayOptions(sections)}
      />
      <SelectField
        id="item-kind"
        label="What kind of item"
        value={form.kind}
        onChange={(kind) => {
          patch({ kind: kind as AddableKind, catalogItemId: "" });
          setChosenName("");
        }}
        options={KIND_OPTIONS.filter((option) => ADDABLE_KINDS.includes(option.value))}
      />
      {form.kind === "CATALOG_ITEM" ? (
        <FieldGroup legend="Find a published catalog item">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
            <TextField
              id="item-catalog-words"
              label="Search words"
              type="search"
              value={words}
              onChange={setWords}
              error={errors.catalog}
            />
            <Button variant="outline" onClick={() => setSearched(words)}>
              Search catalog
            </Button>
          </div>
          {search.isFetching ? <p className={hintClass}>Searching…</p> : null}
          {search.isError ? (
            <Notice tone="danger" role="alert">
              <p>The catalog search failed. Try again.</p>
            </Notice>
          ) : null}
          {search.data !== undefined && search.data.items.length === 0 ? (
            <p className={emptyNoteClass}>No published item matches those words.</p>
          ) : null}
          <ul className="m-0 grid list-none gap-2 p-0" aria-label="Catalog results">
            {(search.data?.items ?? []).map((result) => (
              <li key={result.id}>
                <Button
                  variant={form.catalogItemId === result.id ? "default" : "outline"}
                  className="h-auto min-h-11 w-full justify-start py-2 text-left whitespace-normal"
                  aria-pressed={form.catalogItemId === result.id}
                  onClick={() => {
                    patch({ catalogItemId: result.id });
                    setChosenName(result.name);
                  }}
                >
                  {result.name}
                  {result.locality === null ? "" : `, ${result.locality}`} (
                  {result.itemType.toLowerCase()})
                </Button>
              </li>
            ))}
          </ul>
          {chosenName === "" ? null : (
            <Notice tone="success">
              <p>Chosen: {chosenName}</p>
            </Notice>
          )}
        </FieldGroup>
      ) : (
        <>
          <TextField
            id="item-title"
            label="Title"
            value={form.title}
            onChange={(title) => patch({ title })}
            maxLength={200}
            required
            error={errors.title}
            hint="The customer reads this."
          />
          <TextField
            id="item-location"
            label="Place (typed words)"
            value={form.locationName}
            onChange={(locationName) => patch({ locationName })}
            maxLength={200}
          />
          <TextField
            id="item-locality"
            label="Town or city"
            value={form.locality}
            onChange={(locality) => patch({ locality })}
            maxLength={100}
          />
          <TextField
            id="item-country"
            label="Country code"
            value={form.countryCode}
            onChange={(countryCode) => patch({ countryCode: countryCode.toUpperCase() })}
            maxLength={2}
            error={errors.country}
            hint="Two letters, for example PT."
          />
        </>
      )}
      <TimingFields
        value={form.timing}
        onChange={(timing) => {
          patch({ timing });
          command.clearTimeFailure();
        }}
        errors={errors}
        failure={command.failure}
        defaults={{ date: section?.localDate ?? "", timeZone: section?.timeZone ?? "" }}
        allowEnd
      />
      <SelectField
        id="item-status"
        label="Status"
        value={form.itemStatus}
        onChange={(itemStatus) => patch({ itemStatus: itemStatus as NewItemForm["itemStatus"] })}
        options={STATUS_OPTIONS}
      />
      <FlagFields value={form} onChange={patch} />
      <SaveSettings
        status={context.detail.itinerary.status}
        value={save}
        onChange={setSave}
        reasonRequired={reasonRequired}
        reasonError={errors["itinerary-reason"]}
      />
    </ItineraryCommandDialog>
  );
}

// --- edit ---------------------------------------------------------------------------------------------------------------------

type EditProps = Readonly<{
  context: PlanContext;
  item: Item;
  section: Section;
  onDone: Done;
  onClose: () => void;
}>;

export function EditItemDialog({ context, item, section, onDone, onClose }: EditProps) {
  const [form, setForm] = useState<EditForm>(() => editFormOf(item));
  const [save, setSave] = useState<SaveState>(initialSave);
  const [errors, setErrors] = useState<FieldErrors>({});
  const command = useItineraryCommandForm<StaffItineraryOutcome>(onDone);
  const patch = (next: Partial<EditForm>) => setForm((current) => ({ ...current, ...next }));
  const reasonRequired = reasonNeeded(
    context,
    save,
    touchesConsequentialItem(item),
    command.failure?.kind === "needs_reason",
  );

  function submit() {
    const changes = changesOf(item, form);
    const found: Record<string, string> = { ...editItemErrors(form) };
    if (changes === null) found.nothing = "Nothing changed.";
    if (reasonRequired && save.reason.trim() === "") found["itinerary-reason"] = "Add a reason.";
    setErrors(found);
    if (Object.keys(found).length > 0) return focusFirstInvalidInDialog();
    if (changes === null) return;
    void command.submit(() => sendOperations(context, [editItemOperation(item, changes)], save));
  }

  return (
    <ItineraryCommandDialog
      stale={context.stale}
      title="Edit this item"
      description="Only the fields you change are sent. Every other field and every other item stays as it is."
      confirmLabel="Save changes"
      command={command}
      onSubmit={submit}
      onClose={onClose}
    >
      <TextField
        id="item-title"
        label="Title"
        value={form.title}
        onChange={(title) => patch({ title })}
        maxLength={200}
        required
        error={errors.title}
        hint="The customer reads this."
      />
      <SelectField
        id="item-status"
        label="Status"
        value={form.itemStatus}
        onChange={(itemStatus) => patch({ itemStatus: itemStatus as EditForm["itemStatus"] })}
        options={[...STATUS_OPTIONS, { value: "CANCELLED", label: "Cancelled" }]}
      />
      <TextField
        id="item-location"
        label="Place (typed words)"
        value={form.locationName}
        onChange={(locationName) => patch({ locationName })}
        maxLength={200}
      />
      <TextField
        id="item-locality"
        label="Town or city"
        value={form.locality}
        onChange={(locality) => patch({ locality })}
        maxLength={100}
      />
      <TextField
        id="item-country"
        label="Country code"
        value={form.countryCode}
        onChange={(countryCode) => patch({ countryCode: countryCode.toUpperCase() })}
        maxLength={2}
        error={errors.country}
      />
      <TimingFields
        value={form.timing}
        onChange={(timing) => {
          patch({ timing, timingChanged: true });
          command.clearTimeFailure();
        }}
        errors={errors}
        failure={command.failure}
        defaults={{
          date: section.localDate ?? "",
          timeZone: item.timeZone ?? section.timeZone ?? "",
        }}
        allowEnd
      />
      <FlagFields value={form} onChange={(next) => patch(next)} />
      {errors.nothing === undefined ? null : (
        <Notice tone="danger" role="alert">
          <p>{errors.nothing}</p>
        </Notice>
      )}
      <SaveSettings
        status={context.detail.itinerary.status}
        value={save}
        onChange={setSave}
        reasonRequired={reasonRequired}
        reasonError={errors["itinerary-reason"]}
      />
    </ItineraryCommandDialog>
  );
}

// --- move ---------------------------------------------------------------------------------------------------------------------

type MoveProps = Readonly<{
  context: PlanContext;
  item: Item;
  sections: readonly Section[];
  from: Section;
  onDone: Done;
  onClose: () => void;
}>;

export function MoveItemDialog({ context, item, sections, from, onDone, onClose }: MoveProps) {
  const [form, setForm] = useState<MoveForm>(() => moveFormOf(item, from));
  const [save, setSave] = useState<SaveState>(initialSave);
  const [errors, setErrors] = useState<FieldErrors>({});
  const command = useItineraryCommandForm<StaffItineraryOutcome>(onDone);
  const reasonRequired = reasonNeeded(
    context,
    save,
    touchesConsequentialItem(item),
    command.failure?.kind === "needs_reason",
  );
  const scheduled = item.timingKind === "SCHEDULED";

  function changeDay(id: string) {
    const next = sections.find((entry) => entry.id === id);
    setForm((current) => ({
      ...current,
      sectionId: id,
      // A timed item that changes day starts on the new day's date, in the new day's zone, until staff pick the time.
      start:
        next === undefined || id === from.id
          ? current.start
          : {
              ...current.start,
              date: next.localDate ?? current.start.date,
              timeZone: next.timeZone ?? current.start.timeZone,
              disambiguation: "reject",
            },
      startChanged: id === from.id ? current.startChanged : false,
    }));
  }

  function submit() {
    const found: Record<string, string> = { ...moveErrors(item, form, from) };
    if (reasonRequired && save.reason.trim() === "") found["itinerary-reason"] = "Add a reason.";
    setErrors(found);
    if (Object.keys(found).length > 0) return focusFirstInvalidInDialog();
    void command.submit(() => sendOperations(context, [moveItemOperation(item, from, form)], save));
  }

  return (
    <ItineraryCommandDialog
      stale={context.stale}
      title="Move this item"
      description="Move the item to another day, another time, or both. Its length stays the same."
      confirmLabel="Move item"
      command={command}
      onSubmit={submit}
      onClose={onClose}
    >
      <ZoneDatalist />
      <SelectField
        id="move-day"
        label="Day"
        value={form.sectionId}
        onChange={changeDay}
        options={dayOptions(sections)}
      />
      {scheduled ? (
        <WallTimeFields
          idPrefix="start"
          legend="New start"
          value={form.start}
          onChange={(start) => {
            setForm((current) => ({ ...current, start, startChanged: true }));
            command.clearTimeFailure();
          }}
          errors={errors}
          failure={command.failure}
          takesGapSuggestion
          takesChoice
        />
      ) : (
        <p className={hintClass}>This item has no clock time, so it only changes day.</p>
      )}
      {errors["start-time"] !== undefined && !scheduled ? (
        <Notice tone="danger" role="alert">
          <p>{errors["start-time"]}</p>
        </Notice>
      ) : null}
      <SaveSettings
        status={context.detail.itinerary.status}
        value={save}
        onChange={setSave}
        reasonRequired={reasonRequired}
        reasonError={errors["itinerary-reason"]}
      />
    </ItineraryCommandDialog>
  );
}

// --- remove -------------------------------------------------------------------------------------------------------------------

type RemoveProps = Readonly<{
  context: PlanContext;
  item: Item;
  onDone: Done;
  onClose: () => void;
}>;

export function RemoveItemDialog({ context, item, onDone, onClose }: RemoveProps) {
  const [save, setSave] = useState<SaveState>(initialSave);
  const [errors, setErrors] = useState<FieldErrors>({});
  const command = useItineraryCommandForm<StaffItineraryOutcome>(onDone);
  const reasonRequired = reasonNeeded(
    context,
    save,
    touchesConsequentialItem(item),
    command.failure?.kind === "needs_reason",
  );

  function submit() {
    const found: Record<string, string> = {};
    if (reasonRequired && save.reason.trim() === "") found["itinerary-reason"] = "Add a reason.";
    setErrors(found);
    if (Object.keys(found).length > 0) return focusFirstInvalidInDialog();
    void command.submit(() => sendOperations(context, [removeItemOperation(item)], save));
  }

  return (
    <ItineraryCommandDialog
      stale={context.stale}
      title="Remove this item?"
      description={`"${item.title}" will no longer be in the plan. The earlier revisions keep it.`}
      confirmLabel="Remove item"
      tone="danger"
      command={command}
      onSubmit={submit}
      onClose={onClose}
    >
      {item.conciergeRequestId !== null ? (
        <Notice tone="warning">
          <p>
            This item carries a concierge request. Removing it needs a staff reason, and it is
            refused while the request is open.
          </p>
        </Notice>
      ) : null}
      {item.isFixed ? (
        <Notice tone="warning">
          <p>
            This item is fixed: the customer holds this commitment. Removing it needs a staff
            reason.
          </p>
        </Notice>
      ) : null}
      <SaveSettings
        status={context.detail.itinerary.status}
        value={save}
        onChange={setSave}
        reasonRequired={reasonRequired}
        reasonError={errors["itinerary-reason"]}
      />
    </ItineraryCommandDialog>
  );
}

// --- fixed --------------------------------------------------------------------------------------------------------------------

/** Mark an item as fixed (a commitment the customer holds), or take the mark off. A fixed item asks for a reason on every later change. */
export function FixedDialog({ context, item, onDone, onClose }: RemoveProps) {
  const [save, setSave] = useState<SaveState>(initialSave);
  const [errors, setErrors] = useState<FieldErrors>({});
  const command = useItineraryCommandForm<StaffItineraryOutcome>(onDone);
  const marking = !item.isFixed;
  // Taking the mark off an item touches a fixed item: a staff reason. Marking an item that carries a request does too.
  const reasonRequired = reasonNeeded(
    context,
    save,
    touchesConsequentialItem(item),
    command.failure?.kind === "needs_reason",
  );

  function submit() {
    const found: Record<string, string> = {};
    if (reasonRequired && save.reason.trim() === "") found["itinerary-reason"] = "Add a reason.";
    setErrors(found);
    if (Object.keys(found).length > 0) return focusFirstInvalidInDialog();
    void command.submit(() =>
      sendOperations(context, [editItemOperation(item, { isFixed: marking })], save),
    );
  }

  return (
    <ItineraryCommandDialog
      stale={context.stale}
      title={marking ? "Mark this item as fixed?" : "Remove the fixed mark?"}
      description={
        marking
          ? "A fixed item is a commitment the customer holds, such as a reservation. Later changes to it need a staff reason."
          : "The item stops being a commitment the customer holds. Later changes to it no longer need a reason."
      }
      confirmLabel={marking ? "Mark as fixed" : "Remove fixed mark"}
      command={command}
      onSubmit={submit}
      onClose={onClose}
    >
      <SaveSettings
        status={context.detail.itinerary.status}
        value={save}
        onChange={setSave}
        reasonRequired={reasonRequired}
        reasonError={errors["itinerary-reason"]}
      />
    </ItineraryCommandDialog>
  );
}
